import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { TeamMembersRequestType } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';

import { PaymentCardRequestFeesDocument } from './paymentCardRequestFees.gql';

const VIRTUAL_CARD_REQUESTS_TYPE = [
  TeamMembersRequestType.VirtualPaymentCardOrder,
  TeamMembersRequestType.VirtualBudgetCardOrder,
];

export const usePaymentCardRequestFees = (type: TeamMembersRequestType) => {
  const { companyProfileId } = useCompanyContext();
  const { mainBankAccountId } = useMainBankAccountId();

  if (!companyProfileId) {
    throw new Error(
      "'companyProfileId' is undefined in 'usePaymentCardRequestFees'",
    );
  }

  if (!mainBankAccountId) {
    throw new Error(
      "'mainBankAccountId' is undefined in 'usePaymentCardRequestFees'",
    );
  }

  const isVirtual = VIRTUAL_CARD_REQUESTS_TYPE.includes(type);

  const { data, ...hook } = useQuery(PaymentCardRequestFeesDocument, {
    variables: {
      bankAccountId: mainBankAccountId,
      companyProfileId,
      isVirtual,
    },
  });

  const fees = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const cardFees = isVirtual
      ? {
          monthly: {
            ...data.newVirtualCardPrice,
          },
          order: {
            priceWithoutVAT: 0,
            priceWithVAT: 0,
          },
        }
      : {
          monthly: {
            ...data.premiumCardMonthlyFee,
          },
          order: {
            ...data.newPremiumCardPrice,
          },
        };

    const hasEnoughFunds =
      (cardFees.monthly.priceWithVAT ?? 0) +
        (cardFees.order.priceWithVAT ?? 0) <=
      data.bankAccount.nextBalance;

    return {
      ...cardFees,
      hasEnoughFunds,
    };
  }, [data, isVirtual]);

  return { fees, ...hook };
};
